import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';

// DEPENDENCIES
import compact from 'lodash/compact';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { DASHBOARD } from 'pathnames.js';

// FUNCTIONS
import { preferenceActions } from 'actions.js';
import { apiFetch, buildChartColors, formatDollar, makePath } from 'functions.js';

// CORE COMPONENTS
import Form from 'core/form/Form';

// CONTEXT
export const ReportContext = createContext(null);
export const useReport = () => useContext(ReportContext);

// VARIABLES
const DEFAULT_FILTERS = {
  saleType: 'R',
}

// INITIAL STATE
export const INIT = {
  isError: false,
  isLoading: false,
  errorMessage: ''
};

// REDUCER
export const reportReducer = (state, { type, payload }) => {
  switch (type) {
    case 'INIT':
      return {
        ...state,
        ...INIT,
        isLoading: true,
      };
    case 'ERROR':
      return {
        ...state,
        isError: true,
        errorMessage: 'Unable to run report'
      };
    case 'ABORT':
      return {
        ...state,
        isError: true,
        errorMessage: 'Report aborted'
      };
    case 'LOAD':
      const { result = {}, ...data } = payload || {};
      return {
        ...state,
        ...result,
        data
      };
    case 'COMPLETE':
      return {
        ...state,
        isLoading: false
      };
    case 'FILTER':
      return {
        ...state,
        filters: {
          ...state.filters,
          ...payload
        }
      };
    case 'FLUSH':
      return INIT;
    default:
      return state;
  }
};

// MAIN COMPONENT
export const getReportData = Component => props => {

  // REACT ROUTER
  const navigate = useNavigate();
  const { reportCode, reportID } = useParams();
  const location = useLocation();
  
  // REDUX
  const preferences = useSelector((state) => state.preferences);
  const dispatch = useDispatch();

  // STATE
  const [ context, updateContext ] = useReducer(reportReducer, INIT);

  // MEMOS
  const [
    reportKey,
    isCustom
  ] = useMemo(
    () => [
      `report[${reportCode || reportID}]`,
      !!reportCode
    ],
    [reportCode, reportID]
  )
  const endpoint = useMemo(
    () => isCustom ? ENDPOINTS.dashboard.custom.run(reportCode) : reportID ? ENDPOINTS.dashboard.report.run(reportID) : undefined,
    [reportCode, reportID, isCustom]
  )
  
  const globalFilters = useMemo(() => preferences?.reports?.filters, [preferences]);
  const reportFilters = useMemo(() => preferences[reportKey]?.filters, [preferences, reportKey]);

  // Only listen to local and global filters to properly trigger re-render after filter change. Wait until both filters are initialized.
  const filters = useMemo(
    () => !reportFilters || !globalFilters ? {} : ({ ...reportFilters, ...globalFilters }),
    [reportFilters, globalFilters]
  )

  // FETCH
  const fetchReport = useCallback(
    args => {
      // Both filters should be initialized
      if (!endpoint || isEmpty(filters)) return;
      apiFetch({
        endpoint: endpoint,
        params: filters,
        onError: () => updateContext({ type: 'ERROR' }),
        onSuccess: data => updateContext({ type: 'LOAD', payload: data }),
        onAbort: () => updateContext({ type: 'ABORT' }),
        onComplete: () => updateContext({ type: 'COMPLETE' }),
        loadingMessage: 'Running report',
        errorMessage: 'Unable to run report.',
        abortMessage: 'Report aborted.',
        allowAbort: true,
        ...args
      })
    },
    [endpoint, filters, updateContext]
  )

  useEffect(
    () => {
      fetchReport({
        onFetch: () => updateContext({ type: 'INIT' }),
      });
    },
    [fetchReport]
  );

  // MEMOS
  const charts = useMemo(
    () => {
      const chartReports = context.report_chart ? [context.report_chart] : context.report_charts || [];
      
      return chartReports.map(chartReport => {
        const reportData = chartReport?.chart_data?.result || [];

        if (!compact(reportData.map(r => r.data)).length) return {
          chartReport,
          chartData: []
        }; // If all zeros

        const colors = buildChartColors(reportData.length || 0);
        const getFormat = data => isArray(data[0].data) ? getFormat(data[0].data) : data[0]?.data_format_type;
        const dataFormat = (value, settings) => {
          const localizeValue = value => value % 1 !== 0
          ? value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2, roundingMode: 'trunc' })
          : value.toLocaleString('en-US');
          const formatFunction = getFormat(reportData) === 'currency' ? v => formatDollar(v, settings) : localizeValue;
          return formatFunction(value);
        }

        const transform = data => {
          const dataColors = {};
          return data.map(d => {
            const name = d.label || 'Unknown';
            if (!dataColors[name]) dataColors[name] = d.fillColor || colors[dataColors.length];
            return {
              name,
              color: dataColors[name],
              data: isArray(d.data) ? transform(d.data) : d.data,
              onClick: () => {
                navigate(makePath(location.pathname, DASHBOARD.list), {
                  state: {
                    queryParams: d.queryParam || d.query_param
                  }
                });
              }
            };
          });
        };

        return {
          chartReport,
          chartData: transform(reportData),
          dataFormat,
          axisFormat: n => dataFormat(n, {minimumFractionDigits: 0})
        };
      });
    },
    [context.report_chart, context.report_charts, location, navigate]
  );

  // FILTERS
  const updateFilters = useCallback(
    filters => {
      dispatch(preferenceActions.updatePreference(reportKey, {
        filters
      }))
    },
    [reportKey, dispatch]
  )
  const updateFilter = useCallback(
    (preference, key = reportKey) => {
      dispatch(preferenceActions.updatePreference(key, {
        filters: {
          ...(preferences[key]?.filters || {}),
          ...preference
        }
      }))
    },
    [reportKey, preferences, dispatch]
  )
  
  // Initialize filters.
  const dateFilterOption = useMemo(() => preferences?.reports?.dateFilterOption, [preferences]);
  useEffect(
    () => {
      if (isEmpty(reportFilters)) {
        dispatch(preferenceActions.updatePreference(reportKey, {
          filters: DEFAULT_FILTERS,
        }));
      }
      if (!dateFilterOption) {
        dispatch(preferenceActions.updatePreference('reports', {
          dateFilterOption: 'thisMonth',
          filters: {
            startDate: Form.Date.OPTIONS.thisMonth.startDate,
            endDate: Form.Date.OPTIONS.thisMonth.endDate,
          }
        }));
      }
    },
    [reportKey, dispatch, dateFilterOption, reportFilters]
  )

  // RENDER
  return (
    <ReportContext.Provider
      value={{
        reportCode,
        reportID,
        reportKey,
        isCustom,
        endpoint,
        filters,
        defaults: DEFAULT_FILTERS,
        updateFilter,
        updateFilters,
        fetchReport,
        charts: charts || [],
        ...context
      }}
    >
      <Component {...props} />
    </ReportContext.Provider>
  );
}
